import { Circle, Graphics, Text } from 'pixi.js';

import type { BuildLodgePriceGlowParams, ParsedSeatData, SelectedSeat } from '../models/types';
import type { SeatRenderContext, SeatRenderEntry } from '../models/types';
import type { SeatInfoIndexEntry } from './seatData';
import { getSeatFillColor, getSelectedStrokeColor } from './colors';
import {
  LODGE_AVAILABLE_STROKE_WIDTH,
  LODGE_MARKER_COLOR,
  LODGE_MARKER_GLOW_LAYERS,
  LODGE_MARKER_RADIUS,
  LODGE_PRICE_GLOW_LAYERS,
  LODGE_PRICE_HIGHLIGHT_COLOR,
  PIXI_LODGE_STROKE_SCALE,
} from '../constants/seatMap';

export const buildLodgePriceGlowGraphic = ({ sectorPath }: BuildLodgePriceGlowParams): Graphics => {
  const graphic = new Graphics();
  for (const layer of LODGE_PRICE_GLOW_LAYERS) {
    const strokeWidth =
      layer.strokeWidthMultiplier * LODGE_AVAILABLE_STROKE_WIDTH * PIXI_LODGE_STROKE_SCALE;
    graphic.svg(`
      <svg xmlns="http://www.w3.org/2000/svg">
        <path
          d="${sectorPath}"
          fill="none"
          stroke="${LODGE_PRICE_HIGHLIGHT_COLOR}"
          stroke-opacity="${layer.opacity}"
          stroke-width="${strokeWidth}"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
      </svg>
    `);
  }
  return graphic;
};

const ROYAL_VIP_SEAT_ID_REGEX = /^seatcontainer\.sector\.Royal VIP(?: ?\d+)?\.row\.-\.seat\.-$/i;
const DEFAULT_RADIUS = 12;
const SELECTED_RADIUS = 18;
const SELECTED_INNER_RADIUS = 8;
const HOVER_SCALE = 2.4;
const ROYAL_VIP_HOVER_SCALE = 1.6;
const ROYAL_VIP_RADIUS_MULTIPLIER = 4;

const getSeatLabel = (seat: ParsedSeatData, seatInfo: SeatInfoIndexEntry | null) => {
  if (seat.seat) return String(seat.seat);
  if (seatInfo?.seat) return String(seatInfo.seat);
  return '';
};

export const drawSeatGraphic = (
  graphic: Graphics,
  entry: SeatRenderEntry,
  context: SeatRenderContext
) => {
  const { seat, seatInfo, baseColor, isLodgeMarker } = entry;
  const isSelected = Boolean(seatInfo && context.selectedSeatIds.has(seatInfo.id));
  const isHighlighted =
    !context.hasActivePrices || !seatInfo || context.activePriceSet.has(seatInfo.price);
  const isRoyalVipSeat = ROYAL_VIP_SEAT_ID_REGEX.test(seat.id);
  const isHovered =
    context.hoveredRenderId === entry.renderId && seat.isAvailable && isHighlighted && !isSelected;
  const baseRadius = isLodgeMarker
    ? LODGE_MARKER_RADIUS
    : (isSelected ? SELECTED_RADIUS : DEFAULT_RADIUS) *
      (isRoyalVipSeat ? ROYAL_VIP_RADIUS_MULTIPLIER : 1);
  const hoverScale = isRoyalVipSeat || isLodgeMarker ? ROYAL_VIP_HOVER_SCALE : HOVER_SCALE;
  const radius = baseRadius * (isHovered ? hoverScale : 1);
  const seatLabel = getSeatLabel(seat, seatInfo);

  graphic.clear();
  // Сияние не расширяет область выбора места.
  if (isLodgeMarker) graphic.hitArea = new Circle(seat.x, seat.y, radius);

  if (isLodgeMarker && seat.isAvailable && context.hasActivePrices && isHighlighted) {
    for (const layer of LODGE_MARKER_GLOW_LAYERS) {
      graphic
        .circle(seat.x, seat.y, radius * layer.radiusMultiplier)
        .fill({ color: LODGE_PRICE_HIGHLIGHT_COLOR, alpha: layer.opacity });
    }
  }

  const fillColor = isLodgeMarker ? LODGE_MARKER_COLOR : baseColor;

  if (isSelected) {
    const selectedColor = getSelectedStrokeColor(fillColor);
    graphic.circle(seat.x, seat.y, radius).fill(selectedColor);
    if (!isLodgeMarker) {
      graphic
        .circle(
          seat.x,
          seat.y,
          isRoyalVipSeat
            ? SELECTED_INNER_RADIUS * ROYAL_VIP_RADIUS_MULTIPLIER
            : SELECTED_INNER_RADIUS
        )
        .fill(0xffffff);
    }
  } else {
    graphic
      .circle(seat.x, seat.y, radius)
      .fill(getSeatFillColor(seat.isAvailable, isHighlighted, fillColor));
  }

  if (entry.label) {
    entry.label.text = seatLabel;
    entry.label.visible = Boolean(isHovered && seatLabel && !isLodgeMarker);
    entry.label.x = seat.x;
    entry.label.y = seat.y;
    entry.label.anchor.set(0.5);
    entry.label.style = {
      fontFamily: 'Arial',
      fontSize: Math.max(14, Math.min(22, baseRadius * hoverScale * 1.5)),
      fill: '#ffffff',
      stroke: '#ffffff',
      strokeWidth: 1.5,
      align: 'center',
    };
  }
};

export const buildSeatLabelText = () =>
  Object.assign(
    new Text({
      text: '',
      style: {
        fontFamily: 'Arial',
        fontSize: 14,
        fill: '#ffffff',
        stroke: '#ffffff',
        strokeWidth: 1.5,
        align: 'center',
      },
    }),
    {
      eventMode: 'none' as const,
    }
  );

export const buildSelectedSeatPayload = (
  seat: ParsedSeatData,
  seatInfo: SeatInfoIndexEntry,
  eventId: number
): SelectedSeat => {
  const rowLabel = seat.row ? String(seat.row) : seatInfo.row ? String(seatInfo.row) : '';
  const seatLabel = seat.seat ? String(seat.seat) : seatInfo.seat ? String(seatInfo.seat) : '';

  return {
    seatId: seatInfo.id,
    seatName:
      rowLabel || seatLabel
        ? `Сектор ${seatInfo.sectorName}${rowLabel ? `, Ряд ${rowLabel}` : ''}${seatLabel ? `, Место ${seatLabel}` : ''}`
        : seatInfo.sectorName,
    sectorName: seatInfo.sectorName,
    rowName: rowLabel,
    seatNumber: seatLabel,
    price: seatInfo.price,
    eventId,
    visibility: seatInfo.visibility,
  };
};
